import { useState } from "react";
import {
  useGetAllocationPlanQuery,
  useApplyAllocationMutation,
  useGetAllocationPerformanceQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const sleeveTone: Record<string, string> = {
  aggressive: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  conviction: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  ballast: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

export default function Allocation() {
  const [total, setTotal] = useState(3000);
  const plan = useGetAllocationPlanQuery(total, { pollingInterval: 60000 });
  const perf = useGetAllocationPerformanceQuery(30, { pollingInterval: 60000 });
  const [apply, applyState] = useApplyAllocationMutation();
  const [confirm, setConfirm] = useState(false);

  const p: any = plan.data || {};
  const sleeves: any[] = p.sleeves || [];
  const caps: any = p.engine_caps || {};
  const sleevePerf: any = perf.data?.sleeves || {};

  async function go(dryRun: boolean) {
    const r: any = await apply({ total, dry_run: dryRun }).unwrap();
    if (!dryRun) {
      plan.refetch();
      perf.refetch();
    }
    return r;
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">10× Allocation Policy</h1>
        <p className="text-sm text-muted max-w-2xl">
          Splits capital across the existing engines so high-conviction +
          asymmetric bets compound while ballast smooths the curve. Pre-pump
          and Alpha BSC hunt the 5–10× moves; Conviction DCA compounds the
          slower winners; PAXG grid is the always-on ballast that prevents
          going to zero in a bear cycle.
        </p>
      </header>

      <Card>
        <CardHeader><CardTitle className="text-base">Capital + apply</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 flex-wrap">
            <label className="text-sm">
              <div className="text-muted text-xs mb-1">Total capital (USDT)</div>
              <input type="number" min={100} step={100}
                     className="w-40 bg-bg text-fg border border-border rounded px-2 py-1.5"
                     value={total}
                     onChange={(e) => setTotal(parseFloat(e.target.value) || 3000)} />
            </label>
            <Button variant="ghost" size="sm" disabled={applyState.isLoading}
                    onClick={() => go(true)}>
              Preview (dry-run)
            </Button>
            {!confirm ? (
              <Button size="sm" disabled={applyState.isLoading}
                      onClick={() => setConfirm(true)}>
                Apply…
              </Button>
            ) : (
              <>
                <Button variant="danger" size="sm" disabled={applyState.isLoading}
                        onClick={async () => { await go(false); setConfirm(false); }}>
                  Confirm apply (writes engine settings)
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
                  Cancel
                </Button>
              </>
            )}
          </div>
          {applyState.data && (
            <pre className="mt-3 text-xs bg-bg p-2 rounded overflow-x-auto">
              {JSON.stringify(applyState.data?.applied_keys || applyState.data, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {sleeves.map((s) => (
          <Card key={s.name}>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span className="capitalize">{s.name}</span>
                <Badge className={sleeveTone[s.name] || ""}>
                  {(s.pct * 100).toFixed(0)}%
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="text-2xl font-semibold">${s.usd?.toFixed(0)}</div>
              <div className="text-muted text-xs">{s.description}</div>
              <div className="pt-1">
                {(s.engines || []).map((e: string) => (
                  <span key={e} className="inline-block mr-1 mb-1 px-1.5 py-0.5 text-xs rounded bg-bg border border-border font-mono">
                    {e}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Per-engine caps</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(caps).map(([eng, info]: [string, any]) => (
              <div key={eng} className="border border-border rounded p-3 bg-bg2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-mono text-sm">{eng}</div>
                  <div className="text-sm text-muted">sleeve: <span className="text-fg">${info.sleeve_usd}</span></div>
                </div>
                <div className="text-xs text-muted mt-1">{info.rationale}</div>
                <pre className="text-xs mt-2 overflow-x-auto bg-bg p-2 rounded">
                  {JSON.stringify(info.settings, null, 2)}
                </pre>
                <div className="text-xs text-muted mt-1">
                  Last 30d: <span className="text-fg">{sleevePerf[eng]?.trades ?? 0} trades</span>
                  {" · "}
                  realized P&L:{" "}
                  <span className={`${(sleevePerf[eng]?.realized_pnl || 0) > 0 ? "text-emerald-400" : (sleevePerf[eng]?.realized_pnl || 0) < 0 ? "text-rose-400" : "text-fg"}`}>
                    ${(sleevePerf[eng]?.realized_pnl || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Why this works (math sanity)</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1 text-muted">
          <div>To 10× in 12 months you need ~22%/month compounded.</div>
          <div>Aggressive sleeve hunts 5–10× single trades. If 2 of 10 small bets hit, the sleeve roughly doubles + carries the year.</div>
          <div>Conviction sleeve compounds 2–5× over the year on high-conviction names.</div>
          <div>Ballast preserves capital so a bad month doesn't terminate the run.</div>
          <div className="pt-2 text-fg/80">No "system" guarantees 10×. This stack maximizes the chance the tail outcome happens while bounding the downside.</div>
        </CardContent>
      </Card>
    </div>
  );
}
